import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ClipboardList, Loader2, Plus, X } from 'lucide-react';
import { api } from '../../api/client';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';

/** The roles the template routes let manage forms; the same people work from the office. */
export const NEW_FORM_ROLES = ['admin', 'office_staff', 'supervisor'];

interface TemplateRow {
  id: string;
  name: string | null;
  category: string | null;
  company_id?: string | null;
}

/**
 * "New form" on the Custom Forms tab: pick an active template and open the
 * fill screen (`/forms/dynamic/:id`). Office users are redirected away from
 * the field Forms tab, so without this they had no way to start a form.
 *
 * ACTIVE ONLY. The Records filter lists drafts too, because submissions are
 * filed against unpublished templates; starting a new one is a different
 * question, and an unpublished template is not one the company has released.
 * The tenant's own templates are listed before the global ones.
 */
export default function NewSafetyFormButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fi-action fi-action--standard fi-action--primary"
      >
        <Plus className="size-4" aria-hidden="true" />
        New form
      </button>
      <NewFormPickerDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * The template list behind "New form", and behind Overview's "More forms"
 * tile — one entry point, so the two cannot list different templates.
 */
export function NewFormPickerDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const setOpen = onOpenChange;
  const query = useQuery({
    queryKey: ['safety-templates-list', 'active'],
    queryFn: () => api.safety.templates({ status: 'active' }).then(r => (r.data?.data ?? r.data ?? []) as TemplateRow[]),
    enabled: open,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = query.data === undefined ? queryLoadFailure(query) : null;
  // The tenant's own forms first, then the global library (stable within each).
  const templates = [...(query.data ?? [])].sort((a, b) => Number(!a.company_id) - Number(!b.company_id));

  if (!open) return null;
  /* PORTALLED TO <body>. "New form" sits on the band, and the band is `isolate`:
   * a dialog rendered inside it stacks within the band, so the section strip
   * and search box below drew over the overlay. It also keeps the band's
   * near-white ink rule off the dialog's text. */
  return createPortal(
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <div role="dialog" aria-modal="true" aria-labelledby="new-form-title">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 id="new-form-title" className="text-lg font-semibold text-foreground">Start a form</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="rounded p-1 text-fieldiq-muted hover:text-fieldiq-text"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          {failure ? (
            <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="form templates" />
          ) : query.data === undefined ? (
            <div className="flex justify-center py-6">
              <Loader2 size={20} className="animate-spin text-fieldiq-muted" aria-label="Loading templates" />
            </div>
          ) : templates.length === 0 ? (
            <p className="py-4 text-sm text-fieldiq-text">This company has no active form templates.</p>
          ) : (
            <ul className="divide-y divide-fieldiq-border rounded-xl border border-fieldiq-border" aria-label="Active form templates">
              {templates.map(t => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/forms/dynamic/${t.id}`)}
                    className="flex w-full min-h-[52px] items-center gap-3 px-4 py-3 text-left hover:bg-fieldiq-dark/50"
                  >
                    <ClipboardList size={18} className="shrink-0 text-fieldiq-muted" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{t.name || '—'}</span>
                      <span className="block text-xs capitalize text-fieldiq-muted">
                        {t.category ? t.category.replace(/_/g, ' ') : '—'}
                      </span>
                    </span>
                    <ChevronRight size={16} className="shrink-0 text-fieldiq-muted" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>,
    document.body,
  );
}
